<template>
    <div class="container mx-auto">
        <div class="flex justify-between">
            <div v-if="showHeader" class="flex" :class="[smallHeader ? 'my-3' : 'mb-1']">
                <div class="flex items-center">
                    <div class="flex">
                        <h1 class="page-header" :class="{ 'text-2xl': smallHeader }">Projects</h1>
                        <div class="ml-3 flex items-center text-gray-600">
                            <div :class="[smallHeader ? 'text-lg' : 'text-2xl']">/</div>
                            <div class="mx-2 text-xl" :class="[smallHeader ? 'pt-2px' : 'pt-1']">
                                <template v-if="showFilteredCount && totalAllowedProjects > 0">
                                    {{ projects.filtered.length }} of {{ projects.all.length }} (filtered from {{ totalAllowedProjects }})
                                </template>
                                <template v-else-if="totalAllowedProjects > 0">
                                    {{ projects.all.length }} of {{ totalAllowedProjects }}
                                </template>
                                <template v-else>
                                    {{ projects.filtered.length }} of {{ projects.all.length }}
                                </template>
                            </div>
                        </div>
                    </div>
                    <button v-if="showCreateNew && canCreateProject" @click="addProject()" class="ml-5 btn btn-small btn-blue">
                        <div class="flex items-center justify-center space-x-2">
                            <inline-svg name="add" classes="w-4 h-4 text-white fill-current"></inline-svg>
                            <p class="text-white">New Project</p>
                        </div>
                    </button>
                </div>
            </div>
            <div v-if="showFilters" class="flex items-center" :class="{'flex-1 justify-end pt-4 pb-3': ! showHeader}">
                <filters
                    data-type="projects"
                    sort-by="name"
                    :filters="filters"
                    :selected-view-option="layout"
                    portal="no-projects-found"
                    @view-change="layout = $event"
                    open-search
                >
                    <template slot="filters">
                        <div>
                            <span class="mr-6">Show Completed Projects</span>
                            <toggle-button v-model="showCompletedProjects" color="#38C172" sync></toggle-button>
                        </div>
                    </template>
                </filters>
                <slot name="action-buttons"></slot>
            </div>
        </div>

        <div v-show="layout == 'grid'">
            <paginate name="projects" :list="projects.filtered" :per="perPage" class="paginate-list">
                <div class="mt-5 w-full max-w-lg md:max-w-7xl lg:max-w-[90rem] grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6" :class="{ 'mx-auto': centerCards }">
                    <div class="card" v-for="project in paginated('projects')" :key="project.id">
                        <a
                            :key="project.id"
                            class="h-full relative flex items-stretch text-gray-800 cursor-pointer transform hover:no-underline hover:scale-105"
                            :href="project.view_url"
                        >
                            <div class="w-full flex border rounded-lg shadow-lg">
                                <div class="w-full flex flex-col">
                                    <div class="py-2 px-3 flex flex-1">
                                        <div class="absolute top-0 right-0 badge border rounded rounded-br-none" :class="project.status_color">
                                            <div class="flex items-baseline space-x-1">
                                                <span class="text-base font-variant-all-small-caps">{{ project.status }}</span>
                                            </div>
                                        </div>
                                        <div class="p-2 flex w-full">
                                            <div class="w-full">
                                                <div class="h-full flex flex-col justify-center">
                                                    <div class="flex flex-1 flex-col justify-center">
                                                        <div class="uppercase">
                                                            <p class="text-xl font-bold" :title="project.name">{{ project.name | truncate(25) }}</p>
                                                        </div>
                                                        <div class="flex items-center gap-x-5">
                                                            <table class="flex-1 mt-2 shadow-none border">
                                                                <tbody class="text-gray-900">
                                                                    <tr>
                                                                        <td class="pl-2 font-semibold">Project Type:</td>
                                                                        <td>{{ project.project_type }}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td class="pl-2 font-semibold">Stage:</td>
                                                                        <td>{{ project.stage }}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td class="pl-2 font-semibold">Current Sq. Ft:</td>
                                                                        <td>{{ project.current_sq_ft | number }}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td class="pl-2 font-semibold">Required Sq. Ft:</td>
                                                                        <td>{{ project.required_sq_ft | number }}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td class="pl-2 font-semibold">Property Type:</td>
                                                                        <td>{{ project.property_type }}</td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                            <div class="flex flex-col gap-y-2">
                                                                <div class="flex flex-col items-center justify-center w-20 h-20 p-2 site-title-bar rounded-full shadow-lg font-medium text-lg text-white">
                                                                    <p class="text-xl font-black">{{ project.sites_count }}</p>
                                                                    <p class="text-xs font-bold">{{ project.sites_count | pluralize('Site') }}</p>
                                                                </div>
                                                                <div class="relative w-20 h-20">
                                                                    <div class="absolute -top-2 -right-2">
                                                                        <InfoIcon identifier="projects-comps" />
                                                                    </div>

                                                                    <div class="flex flex-col items-center justify-center w-full h-full p-2 bg-gradient-to-b from-green-500 to-green-600 rounded-full shadow-lg font-medium text-lg text-white">
                                                                        <p class="text-xl font-black">{{ project.comps_count }}</p>
                                                                        <p class="text-xs font-bold">{{ project.comps_count | pluralize('Comp') }}</p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="py-4 px-5 flex justify-between bg-gray-100 rounded-b-lg">
                                        <div class="badge" :class="project.priority_color">
                                            <div class="flex items-baseline space-x-1">
                                                <span class="text-base font-variant-all-small-caps">{{ project.priority }}</span>
                                                <InfoIcon identifier="projects-priority" />
                                            </div>
                                        </div>

                                        <div class="badge" :class="project.benchmark_color">
                                            <div class="flex items-baseline space-x-1">
                                                <span class="font-extrabold">{{ Math.abs(project.benchmark) }}%</span>
                                                <span class="text-base font-variant-all-small-caps">{{ project.benchmark <= 0 ? 'Under' : 'Over' }} Market</span>
                                                <InfoIcon identifier="projects-under-over-market" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </a>
                    </div>
                </div>
            </paginate>
            <paginate-links for="projects" :show-step-links="true" :hide-single-page="true"></paginate-links>
        </div>

        <div v-show="layout == 'table'" :class="{ 'mt-5': (showHeader && ! smallHeader) || showFilters }">
            <table v-if="projects.filtered.length" class="w-full">
                <thead class="cursor-pointer">
                    <tr>
                        <th class="pl-4">Name</th>
                        <th class="hidden lg:table-cell text-center">Start Date</th>
                        <th class="text-center">End Date</th>
                        <th class="hidden lg:table-cell text-right">Sq. Ft.</th>
                        <th class="pl-6 text-center">
                            Remaining
                            <InfoIcon identifier="dashboard-projects-remaining" class="inline-block ml-1" />
                        </th>

                        <th class="hidden lg:table-cell pl-6">
                            Type
                            <InfoIcon identifier="dashboard-projects-type" class="inline-block ml-1" />
                        </th>
                        <th class="hidden lg:table-cell pl-6">Stage</th>
                        <th class="text-right">
                            OM %
                            <InfoIcon identifier="dashboard-projects-om%" class="inline-block ml-1" />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(project, projectIndex) in projects.filtered" :key="projectIndex">
                        <td class="pl-4">
                            <a :href="project.view_url">
                                <p class="font-semibold">{{ project.name }}</p>
                            </a>
                        </td>
                        <td class="hidden lg:table-cell text-center">{{ project.start_date_formatted }}</td>
                        <td class="text-center">{{ project.end_date_formatted }}</td>
                        <td class="hidden lg:table-cell text-right">{{ project.required_sq_ft | number }}</td>
                        <td class="pl-6 text-center">
                            <div class="badge badge-red">
                                <span data-precision="0" :data-originalvalue="project.shortened_remaining_lease_cost" class="is-shorthand-currency text-sm font-extrabold">${{ project.shortened_remaining_lease_cost }}</span>
                            </div>
                        </td>
                        <td class="hidden lg:table-cell pl-6">{{ project.project_type }}</td>
                        <td class="hidden lg:table-cell pl-6">{{ project.stage }}</td>
                        <td class="text-right font-bold">{{ project.benchmark }}%</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <modal name="new-project" width="60%" height="auto" click-to-close draggable=".modal-header">
            <new-project-modal></new-project-modal>
        </modal>

        <portal-target name="no-projects-found"></portal-target>
    </div>
</template>

<script>
    import moment from 'moment';
    import { mapState } from 'vuex';
    export default {
        props: {
            projectData: {
              type: Array,
              required: true
            },

            showHeader: {
              type: Boolean,
              default: true
            },

            smallHeader: {
              type: Boolean,
              default: false
            },

            showFilters: {
              type: Boolean,
              default: true
            },

            showCreateNew: {
              type: Boolean,
              default: false
            },

            centerCards: {
                type: Boolean,
                default: false
            },

            view: {
                type: String,
                default: 'grid'
            },

            perPage: {
                type: Number,
                default: 10
            }
        },

        data() {
            return {
                paginate: ['projects'],
                showCompletedProjects: false,
                layout: this.view,
            }
        },

        mounted() {
            this.$eventHub.$on('reset-search', () => {
                this.paginate.projects.list = this.projects.filtered;
            });

            this.projects.all = this.projectData;
            this.projects.filtered = this.projectData;

            this.$emit('filterRequest');
        },

        computed: {
            ...mapState(['projects']),
            canCreateProject() {
                return this.$gate.can('create', 'project');
            },
            showFilteredCount() {
                // Show filtered count when filters are applied, regardless of whether there are results
                return this.projects.filtered.length !== this.projects.all.length;
            },
            
            totalAllowedProjects() {
                // Get the module data for projects
                const moduleData = this.$store.getters['moduleAccess/getModuleBySlug']('projects');
                return moduleData?.subscription_details?.quantity || 0;
            },

            filters() {
                return {
                    showCompletedProjects: this.showCompletedProjects
                }
            }
        },

        methods: {
            dueIn(project_end_date) {
                return moment(project_end_date).isBefore(moment()) ? 'Overdue' : 'Due ' + moment(project_end_date).fromNow()
            },    
            addProject() {
                const moduleSlug = 'projects';
                const newEntityModalName = 'new-project';
                
                // Use the global module access overlay
                this.$moduleAccessOverlay.show(moduleSlug, {
                    targetModal: newEntityModalName
                });
            }
        }
    }
</script>